import { MutableRefObject, useEffect } from 'react';
import { Text, Flexbox } from '@morressier/ts';
import omit from 'lodash.omit';
import {
  addRinggoldAffiliation,
  addUserProvidedAffiliation,
  patchAffiliation,
  updateUserActivation
} from '../../hooks';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { faCircleInfo as faCircleInfoRegular } from '@fortawesome/pro-regular-svg-icons';

import { isBefore } from 'date-fns';
import {
  faChevronRight,
  faChevronLeft,
  faSpinnerThird
} from '@fortawesome/pro-regular-svg-icons';
import { IAffiliation, IAffiliationResponse } from 'typings/organization';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, ActionModalContent } from 'components/ProfileUIComponents';
import { updatePartialProfileGeneralInfo } from 'clients/internal/profileService';
import { VIEWS } from '../EmailAffiliation';
import { useRouter } from 'next/router';
import { getQuery } from 'utils/query';
import {
  Container,
  MediumInput,
  MonthPicker,
  StartDatePicker,
  Loader,
  ModalForm,
  Info,
  StyledCheckbox
} from '../style';
import { logger } from 'utils/logger';
import { typedBoolean } from 'utils/utils';
import { format } from 'date-fns';
import { IProfile } from 'typings/profile';
import { KeyedMutator } from 'swr';
import { logEvent } from 'utils/eventTracking';

interface IDetailProps {
  affiliationData: IAffiliation;
  handleNavigaiton: (nextView: VIEWS) => void;
  onDismiss: () => void;
  isUserProvidedAffiliation: boolean;
  showEditView: boolean;
  onExit: (affilaitonData: IAffiliation) => void;
  isPrimaryAffiliation: boolean;
  userId: string;
  mutateProfile: KeyedMutator<IProfile>;
  refetchAffiliations: KeyedMutator<IAffiliationResponse>;
  isFormDirty: MutableRefObject<boolean>;
  isWithoutEmailAccess: boolean;
  updateEmailDomainMatchesOrgDomain: (
    isEmailDomainMatchesOrgDomain: boolean
  ) => void;
}

interface FormValues {
  organizationId: string;
  role: string;
  startDate: string;
  curentlyWorking: boolean;
  markAsPrimaryAffiliation: boolean;
  endDate: string;
  refetchAffiliations: KeyedMutator<IAffiliationResponse>;
}
const DetailView = (props: IDetailProps) => {
  const router = useRouter();
  const queryParams = getQuery(router);
  const isAccountActivation = queryParams.flow === 'account-activation';

  const {
    showEditView,
    affiliationData,
    isPrimaryAffiliation,
    userId,
    mutateProfile,
    refetchAffiliations,
    isFormDirty,
    updateEmailDomainMatchesOrgDomain
  } = props;

  const { startDate, endDate } = affiliationData;

  const defaultValues = showEditView
    ? {
        ...affiliationData,
        startDate: format(Date.parse(startDate), 'yyyy-MM'),
        endDate:
          endDate && endDate.length > 0
            ? format(Date.parse(endDate), 'yyyy-MM')
            : '',
        curentlyWorking: !endDate,
        markAsPrimaryAffiliation: isPrimaryAffiliation
      }
    : { markAsPrimaryAffiliation: isPrimaryAffiliation };

  const {
    handleSubmit,
    clearErrors,
    watch,
    control,
    setValue,
    formState: { errors, isDirty }
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    if (showEditView) {
      isFormDirty.current = isDirty;
    }
  }, [isDirty]);

  const {
    handleNavigaiton,
    onDismiss,
    isUserProvidedAffiliation,
    isWithoutEmailAccess
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const actionName = showEditView ? 'Edit' : 'Add';

  const onSecondaryClick = () => {
    logEvent(
      'PROFILE_ADD_AFFILIATION_AFFILIATION_DETAILS_UPDATE_ORGANIZATION_CLICKED'
    );
    if (isWithoutEmailAccess) {
      handleNavigaiton(
        isUserProvidedAffiliation
          ? VIEWS.UserProvidedAffiliationView
          : VIEWS.SearchOrganizationView
      );
    } else {
      handleNavigaiton(
        isUserProvidedAffiliation
          ? VIEWS.UserProvidedAffiliationView
          : VIEWS.OrganizationView
      );
    }
  };

  const addUserAffiliation = async (data: FormValues) => {
    const { role, startDate, endDate = '', markAsPrimaryAffiliation } = data;
    const { organizationId, email } = affiliationData;
    try {
      setIsLoading(true);
      let response: IAffiliation;
      if (isUserProvidedAffiliation) {
        response = await addUserProvidedAffiliation({
          ...affiliationData,
          ...omit(data, ['curentlyWorking'])
        });
      } else {
        response = await addRinggoldAffiliation(
          organizationId,
          email,
          role,
          startDate,
          endDate
        );
      }
      updateEmailDomainMatchesOrgDomain(response.isEmailDomainMatchesOrgDomain);

      if ((markAsPrimaryAffiliation || isAccountActivation) && response) {
        await updatePartialProfileGeneralInfo(userId, {
          primaryAffiliation: response.id
        });

        logEvent('PROFILE_ADD_PRIMARY_AFFILIATION', {
          affiliation_id: response?.id
        });
        await mutateProfile();
      }
      if (isAccountActivation) {
        await updateUserActivation(userId, { is_activated: true });
      }
      await refetchAffiliations();
      setIsLoading(false);
      handleNavigaiton(VIEWS.EmailValidationView);
    } catch (error: any) {
      const errData = error?.response?.data?.errors;
      const errorMsg =
        typeof errData === 'object' ? JSON.stringify(errData) : errData;

      alert(errorMsg);
      setIsLoading(false);
      logger.error('error during updating affiliation', error);
    }
  };

  const updateUserAffiliation = async (data: FormValues) => {
    const {
      role,
      startDate,
      endDate,
      curentlyWorking,
      organizationId,
      markAsPrimaryAffiliation
    } = data;
    logEvent('PROFILE_EDIT_AFFILIATION_UPDATE_AFFILIATION_CLICKED', {
      affiliation_id: affiliationData.id,
      organization_id: organizationId
    });
    const { id } = affiliationData;
    try {
      setIsLoading(true);
      const sDate = new Date(startDate);
      const eDate =
        endDate.length > 0 && curentlyWorking == false ? new Date(endDate) : '';
      const type =
        !organizationId || organizationId.length == 0
          ? 'USER_PROVIDED'
          : 'RINGGOLD';
      const response = await patchAffiliation(
        id,
        role,
        sDate.toString(),
        eDate.toString(),
        type
      );

      if (!defaultValues.markAsPrimaryAffiliation && markAsPrimaryAffiliation)
        logEvent('PROFILE_ADD_PRIMARY_AFFILIATION', {
          affiliation_id: id
        });
      else if (
        defaultValues.markAsPrimaryAffiliation &&
        !markAsPrimaryAffiliation
      )
        logEvent('PROFILE_REMOVE_PRIMARY_AFFILIATION', {
          affiliation_id: id
        });

      await updatePartialProfileGeneralInfo(userId, {
        primaryAffiliation: markAsPrimaryAffiliation ? id : null
      });
      await mutateProfile();
      await refetchAffiliations();

      setIsLoading(false);
      props.onExit({ ...response.affiliation });
    } catch (error) {
      setIsLoading(false);
      logger.error('error during adding affiliation', error);
    }
  };

  const onSubmit = (data: FormValues) => {
    logEvent('PROFILE_ADD_AFFILIATION_AFFILIATION_DETAILS_CONTINUE_CLICKED', {
      organization_id: data.organizationId
    });
    if (showEditView == true) updateUserAffiliation(data);
    else addUserAffiliation(data);
  };

  const desc =
    queryParams.flow === 'account-activation'
      ? 'Tell your community how you currently participate in or contribute to scientific research. You will able to edit this later.'
      : 'Tell your community how you contribute to or participate in scientific research.';

  return (
    <ModalForm onSubmit={handleSubmit(onSubmit)}>
      <ActionModalContent
        title={actionName + ' affiliation'}
        primaryAction={{
          title: showEditView ? 'Update affiliation' : 'Continue',
          iconAfter: isLoading ? (
            <Loader icon={faSpinnerThird} />
          ) : (
            <FontAwesomeIcon icon={faChevronRight} />
          ),
          disabled: isLoading,
          type: 'submit'
        }}
        {...(!showEditView && {
          secondaryAction: {
            title: 'Update organization',
            iconBefore: <FontAwesomeIcon icon={faChevronLeft} />,
            onClick: onSecondaryClick,
            type: 'button'
          }
        })}
        cautionaryAction={{
          title: 'Exit without saving',
          onClick: () => {
            logEvent('PROFILE_AFFILIATION_EXIT_WITHOUT_SAVING_CLICKED', {
              step: 'ADD_DETAILS',
              affiliation_id: affiliationData.id
            });
            onDismiss();
          },
          type: 'button'
        }}
      >
        <form>
          <Container>
            <Text size="h3_new" fontWeight="bold">
              {actionName} your details
            </Text>
            <Text>
              {showEditView
                ? 'To make changes to your email or organization you need to delete this affiliation and create a new one.'
                : desc}
            </Text>
          </Container>

          {isPrimaryAffiliation && !watch('markAsPrimaryAffiliation') && (
            <Container>
              <Info>
                <FontAwesomeIcon icon={faCircleInfoRegular} />
                <Text>
                  Set your new primary affiliation in the Affiliation section of
                  your profile.
                </Text>
              </Info>
            </Container>
          )}
          <Container>
            <Text fontWeight="bold">Your role</Text>
            <Controller
              name="role"
              control={control}
              rules={{
                required: 'Role is Required',
                maxLength: {
                  value: 255,
                  message:
                    'Role value exceeds maximum length of 255 characters.'
                }
              }}
              render={({ field }) => (
                <MediumInput
                  placeholder="e.g. Research Fellow"
                  {...field}
                  error={errors.role?.message}
                />
              )}
            />
          </Container>
          <Container>
            <Text fontWeight="bold">Start Date</Text>
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: 'Start Date is Required',
                deps: 'endDate'
              }}
              render={({ field }) => (
                <StartDatePicker
                  placeholder="choose a date"
                  type="month"
                  {...field}
                  error={errors.startDate?.message}
                />
              )}
            />
          </Container>

          {!isAccountActivation && (
            <>
              <Container>
                <Text fontWeight="bold">End Date</Text>

                <Controller
                  name="endDate"
                  control={control}
                  rules={{
                    validate: endDate => {
                      const startDate = watch('startDate');
                      const currentlyWorking = watch('curentlyWorking');
                      if (currentlyWorking) return true;
                      if (!endDate && !currentlyWorking)
                        return 'You must select either Currently work or End Date';
                      else if (
                        isBefore(new Date(endDate), new Date(startDate))
                      ) {
                        return "End date shouldn't be before Start date.";
                      }
                    }
                  }}
                  render={({ field }) => (
                    <MonthPicker
                      type="month"
                      {...field}
                      disabled={watch('curentlyWorking')}
                      placeholder="choose a date"
                      placement="bottom"
                      error={errors.endDate?.message}
                      onChange={value => {
                        field.onChange(value);
                        if (value) setValue('markAsPrimaryAffiliation', false);
                      }}
                    />
                  )}
                />
                {!isAccountActivation && (
                  <Controller
                    name="curentlyWorking"
                    control={control}
                    rules={{ deps: 'endDate' }}
                    render={({ field }) => (
                      <Checkbox
                        defaultChecked={showEditView && !endDate}
                        checked={field.value}
                        onChange={e => {
                          clearErrors('endDate');
                          setValue('endDate', '');
                          field.onChange(e.target.checked);
                        }}
                      >
                        I currently work in this role
                      </Checkbox>
                    )}
                  />
                )}
              </Container>

              <Text fontWeight="bold">Primary affiliation</Text>
              <Text size="body1_new">
                Only one current affiliation can be selected as primary.
              </Text>
              <Controller
                name="markAsPrimaryAffiliation"
                control={control}
                rules={{ deps: 'endDate' }}
                render={({ field }) => (
                  <StyledCheckbox
                    defaultChecked={showEditView && !endDate}
                    checked={field.value}
                    disabled={typedBoolean(watch('endDate'))}
                    onChange={e => {
                      field.onChange(e.target.checked);
                    }}
                  >
                    Display this as the primary affiliation on your profile
                  </StyledCheckbox>
                )}
              />
            </>
          )}
        </form>
      </ActionModalContent>
    </ModalForm>
  );
};
export default DetailView;
